import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect } from 'react';
import { useLoginContext } from '../../../../../contexts/LoginContext';
import { StepperComponentProps } from '../../../NewAccountStepper/types';
import { useBaseStepperContext } from '../../contexts/BaseStepperContext';
import { stepperStyles } from '../../styles';
import TimeSpanList from './TimeSpanList';
import { TimeSpan } from './types';
import { daysofweek } from './utils';

const defaultStart = '2022-04-17T06:00';
const defaultEnd = '2022-04-17T19:00';

const TimeSpans: React.FC<StepperComponentProps> = ({
  description,
  stepIndex,
  label,
}) => {
  const { user, updateProfile, selectedProfileIndex, setError, setUser } =
    useLoginContext();

  const { updateStepValidation } = useBaseStepperContext();

  const selectedProfile = user?.extProfiles[selectedProfileIndex ?? 0];
  const [disabled, setDisabled] = React.useState(true);

  // Determine the correct step index for validation
  // If stepIndex prop is provided, use it; otherwise default to 0 for ModifyAccountStepper
  const validationStepIndex = stepIndex !== undefined ? stepIndex : 0;

  const [days, setDays] = React.useState<string[]>([]);
  const [startTime, setStartTime] = React.useState<Dayjs | null>(
    dayjs(defaultStart)
  );
  const [endTime, setEndTime] = React.useState<Dayjs | null>(dayjs(defaultEnd));

  const getMissingFields = React.useCallback(() => {
    if (
      selectedProfileIndex !== undefined &&
      user?.extProfiles[selectedProfileIndex]?.timeSpans?.length === 0
    ) {
      return ['At least one time span'];
    }
    return [];
  }, [selectedProfileIndex, user?.extProfiles]);

  const missingFields = React.useMemo(
    () => getMissingFields(),
    [getMissingFields]
  );
  const hasErrors = React.useMemo(
    () => missingFields.length > 0,
    [missingFields]
  );

  const warningMessage = React.useMemo(() => {
    if (missingFields.length === 0) return '';
    return 'Please add at least one time span to receive notifications';
  }, [missingFields]);

  // Update step validation whenever validation state changes
  useEffect(() => {
    updateStepValidation(validationStepIndex, warningMessage, hasErrors);
  }, [updateStepValidation, hasErrors, warningMessage, validationStepIndex]);

  useEffect(() => {
    if (
      selectedProfileIndex !== undefined &&
      user?.extProfiles[selectedProfileIndex]?.timeSpans?.length === 0
    ) {
      setError(true);
    } else {
      setError(false);
    }
  }, [user, setError, selectedProfileIndex]);

  const addTimeSpan = useCallback(() => {
    if (selectedProfile) {
      const timeSpans = selectedProfile.timeSpans || [];
      const newTimeSpan = {
        startHour: startTime?.hour() ?? 0,
        startMinute: startTime?.minute() ?? 0,
        endHour: endTime?.hour() ?? 0,
        endMinute: endTime?.minute() ?? 0,
        days: days.join(''),
      };
      const timeSpansEqual = timeSpans.some((span) => {
        return isEqual(span, newTimeSpan); // Check if the time span is equal and days are equal
      });
      if (!timeSpansEqual) {
        timeSpans.push(newTimeSpan);
        selectedProfile.timeSpans = timeSpans;
        updateProfile(selectedProfile);
      }
    }
    setDays([]);
    setStartTime(dayjs(defaultStart));
    setEndTime(dayjs(defaultEnd));
  }, [selectedProfile, startTime, endTime, days, updateProfile]);

  useEffect(() => {
    if (days.length === 0) {
      setDisabled(true);
      return;
    }
    if (selectedProfile) {
      const timeSpans = selectedProfile.timeSpans || [];
      const newTimeSpan = {
        startHour: startTime?.hour() ?? 0,
        startMinute: startTime?.minute() ?? 0,
        endHour: endTime?.hour() ?? 0,
        endMinute: endTime?.minute() ?? 0,
        days: days.join(''),
      };
      const timeSpansEqual = timeSpans.some((span) => {
        return isEqual(span, newTimeSpan); // Check if the time span is equal and days are equal
      });
      setDisabled(timeSpansEqual);
    }
  }, [startTime, endTime, days, selectedProfile, setDisabled]);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('md'));

  const timeSpans =
    user?.extProfiles[selectedProfileIndex ?? 0].timeSpans || [];

  const handleDelete = useCallback(
    (id: number) => {
      const timeSpans =
        user?.extProfiles[selectedProfileIndex ?? 0].timeSpans || [];
      const newTimeSpans = timeSpans.filter((span: TimeSpan, index: number) => {
        return index !== id;
      });
      const updatedProfile = {
        ...user?.extProfiles[selectedProfileIndex ?? 0],
        timeSpans: newTimeSpans,
      };
      const updatedUser = {
        ...user,
        extProfiles: user.extProfiles.map((profile, index) =>
          index === selectedProfileIndex ? updatedProfile : profile
        ),
      };
      setUser(updatedUser);
    },
    [user, selectedProfileIndex, setUser]
  );

  return (
    <Box sx={stepperStyles.pageContainer}>
      <Box sx={stepperStyles.centeredContent}>
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          sx={stepperStyles.pageTitle(theme)}
        >
          {label}
        </Typography>
        <Typography variant="body1" sx={stepperStyles.pageDescription(theme)}>
          {description}
        </Typography>
        <Paper elevation={3} sx={stepperStyles.elevatedCard(theme)}>
          <Typography
            variant="body2"
            sx={{
              ...stepperStyles.helperNote(theme),
              mb: 4,
            }}
          >
            Incidents are only reported from 6 AM to 7 PM, Monday through
            Friday.
          </Typography>

          <Box sx={stepperStyles.timeConfigContainer}>
            <TimePicker
              value={startTime}
              label="Start Time"
              onChange={(date) => setStartTime(date)}
              slotProps={{ textField: { size: 'small' } }}
            />

            <TimePicker
              value={endTime}
              label="End Time"
              onChange={(date) => setEndTime(date)}
              slotProps={{ textField: { size: 'small' } }}
            />

            <Box sx={stepperStyles.checkboxGroup}>
              {daysofweek.map((day) => (
                <FormControlLabel
                  key={day.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={days.includes(day.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setDays((prev) => [...prev, day.value]);
                        } else {
                          setDays((prev) =>
                            prev.filter((d) => d !== day.value)
                          );
                        }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={stepperStyles.infoNote}>
                      {matches ? day.name : day.abbr}
                    </Typography>
                  }
                  sx={stepperStyles.checkboxLabel}
                />
              ))}
            </Box>

            <Button
              disabled={disabled}
              variant="contained"
              endIcon={<AddIcon />}
              onClick={addTimeSpan}
              sx={stepperStyles.addButton}
            >
              Add
            </Button>
          </Box>

          <TimeSpanList timeSpans={timeSpans} onDelete={handleDelete} />
        </Paper>
      </Box>
    </Box>
  );
};

export default TimeSpans;
